import React from "react";
import logoUrl from "./assets/meditec-logo.jpg";
import { fetchSession, login, logout } from "./api/auth";
import { resetTimeFormat } from "./hooks/useTimeFormat";
import LoginForm from "./components/auth/LoginForm";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import ResetPasswordForm from "./components/auth/ResetPasswordForm";
import AdminDashboard from "./components/admin/AdminDashboard";
import ContractorDashboard from "./components/contractor/ContractorDashboard";

function getResetToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") ?? null;
}

function clearResetToken() {
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  window.history.replaceState(null, "", url.toString());
}

export default function App() {
  const [session, setSession] = React.useState(null);
  const [isBootstrapping, setIsBootstrapping] = React.useState(true);
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [authError, setAuthError] = React.useState("");
  const [view, setView] = React.useState(() => {
    return getResetToken() ? "reset" : "login";
  });
  const [resetToken] = React.useState(getResetToken);

  React.useEffect(() => {
    // Skip session check when landing on the reset-password page
    if (view === "reset") {
      setIsBootstrapping(false);
      return;
    }

    let isMounted = true;

    fetchSession()
      .then((nextSession) => {
        if (isMounted) {
          setSession(nextSession);
        }
      })
      .catch((error) => {
        if (isMounted && error?.status !== 401) {
          console.error(error);
          setAuthError("Unable to restore your session. Please sign in again.");
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsBootstrapping(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = async (credentials) => {
    setIsLoggingIn(true);
    setAuthError("");

    try {
      const nextSession = await login(credentials);
      resetTimeFormat();
      setSession(nextSession);
    } catch (error) {
      console.error(error);
      setAuthError(
        error?.status === 401
          ? "Invalid email or password."
          : error?.message ?? "Unable to sign in right now."
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error(error);
    } finally {
      setSession(null);
      setAuthError("");
    }
  };

  const handleResetSuccess = () => {
    clearResetToken();
    setView("reset_success");
  };

  if (isBootstrapping) {
    return (
      <div className="auth-shell">
        <section className="auth-card auth-card-loading">
          <div className="auth-branding">
            <img className="auth-logo" src={logoUrl} alt="Meditec Staffing Company" />
            <h1>Loading portal...</h1>
          </div>
          <p>Checking your session and preparing the right workspace.</p>
        </section>
      </div>
    );
  }

  if (view === "reset") {
    return (
      <ResetPasswordForm
        logoUrl={logoUrl}
        token={resetToken}
        onSuccess={handleResetSuccess}
        onResendLink={() => { clearResetToken(); setView("forgot"); }}
      />
    );
  }

  if (view === "reset_success") {
    return (
      <div className="auth-shell">
        <section className="auth-card" aria-labelledby="reset-success-title">
          <div className="auth-branding">
            <img className="auth-logo" src={logoUrl} alt="Meditec Staffing Company" />
            <h1 id="reset-success-title">Timecard portal</h1>
          </div>
          <div className="auth-confirm">
            <div className="auth-confirm-icon" aria-hidden="true">✓</div>
            <p className="auth-confirm-title">Password reset successful</p>
            <p className="auth-confirm-body">Your password has been reset. You can now sign in with your new password.</p>
            <button
              type="button"
              className="primary-action auth-submit"
              onClick={() => { setAuthError(""); setView("login"); }}
            >
              Sign in
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!session) {
    if (view === "forgot") {
      return (
        <ForgotPasswordForm
          logoUrl={logoUrl}
          onBack={() => setView("login")}
        />
      );
    }

    return (
      <LoginForm
        onLogin={handleLogin}
        isSubmitting={isLoggingIn}
        errorMessage={authError}
        logoUrl={logoUrl}
        onForgotPassword={() => setView("forgot")}
      />
    );
  }

  if (session.role === "admin") {
    return (
      <AdminDashboard
        session={session}
        onLogout={handleLogout}
        logoUrl={logoUrl}
      />
    );
  }

  return (
    <ContractorDashboard
      session={session}
      onLogout={handleLogout}
      logoUrl={logoUrl}
    />
  );
}
